// CSDB Seed Pack — zip bundle with BREX skeleton, SNS XML and readiness report.
import JSZip from "jszip";
import { buildBrexSkeletonXml } from "./brexSkeleton";
import { findSnsPreset, FX_SUPPORTED_CODES } from "./s1000dData";
import { buildDmcInventoryFromSbs, buildDmcInventoryCsv } from "./sbsDmc";

function xmlEscape(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function safeFilename(name) {
  return (name || "estimate").replace(/[^a-z0-9-_]+/gi, "_");
}

export { safeFilename };

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export { triggerDownload };

function buildSnsXml(presetId) {
  const p = findSnsPreset(presetId);
  const systems = p.systems.map((s) => {
    const subs = s.subsystems.map((ss) => {
      const subsubs = ss.subsubsystems.map((sss) =>
        `        <subsubsystem code="${xmlEscape(sss.code)}" name="${xmlEscape(sss.name)}"/>`).join("\n");
      return `      <subsystem code="${xmlEscape(ss.code)}" name="${xmlEscape(ss.name)}">\n${subsubs}\n      </subsystem>`;
    }).join("\n");
    return `    <system code="${xmlEscape(s.code)}" name="${xmlEscape(s.name)}">\n${subs}\n    </system>`;
  }).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<!-- S1000D SNS — ${xmlEscape(p.name)} (generated by S1000D Cost Estimator) -->
<sns id="${xmlEscape(p.id)}" name="${xmlEscape(p.name)}">
${systems}
</sns>
`;
}

function buildReadinessMarkdown(readiness, estimate, totals, resourcing, brex, dml) {
  const sym = estimate.currency?.symbol || "$";
  const fc = (v) => `${sym}${Number(v || 0).toFixed(2)}`;
  const lines = [];
  lines.push(`# Bid Readiness Scorecard`);
  lines.push("");
  lines.push(`**Project:** ${estimate.projectName || "Untitled"}`);
  lines.push(`**Generated:** ${new Date().toISOString()}`);
  lines.push(`**Overall:** ${readiness.overall.toUpperCase()} · Score ${readiness.score}/100`);
  lines.push(`**Counts:** ${readiness.counts.pass} pass · ${readiness.counts.warn} warn · ${readiness.counts.fail} fail`);
  lines.push("");
  lines.push(`## Headline Numbers`);
  lines.push(`- Total DMs: ${totals.totalDms} · Illustrations: ${totals.totalIllus}`);
  lines.push(`- Total Project Hours: ${totals.totalHours.toFixed(1)}`);
  lines.push(`- Base Cost: ${fc(totals.baseCost)} · Grand Total: ${fc(totals.grandTotal)}`);
  lines.push(`- Working Days: ${resourcing.workingDays} · Required FTE: ${resourcing.fteEffective.toFixed(2)}`);
  lines.push(`- BREX scope: ${brex.totalDps} DPs · ${brex.totalHours.toFixed(1)} hrs · ${fc(brex.grandTotal)}`);
  if (dml) lines.push(`- DML scope: ${dml.dmcCount} DMC / ${dml.icnCount} ICN / ${dml.pmcCount} PMC · ${dml.totalHours.toFixed(1)} hrs · ${fc(dml.grandTotal)}`);
  lines.push("");
  const byDim = {};
  for (const c of readiness.checks) {
    byDim[c.dimension] = byDim[c.dimension] || [];
    byDim[c.dimension].push(c);
  }
  lines.push(`## Checks`);
  for (const [dim, items] of Object.entries(byDim)) {
    lines.push(`### ${dim}`);
    for (const c of items) {
      const icon = c.status === "pass" ? "✓" : c.status === "warn" ? "⚠" : "✗";
      lines.push(`- ${icon} **${c.label}** — ${c.status.toUpperCase()} · ${c.message}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

// ---- Shared seed-pack builder ----
export function _buildSeedPackZip({ estimate, totals, resourcing, brex, dml, readiness }) {
  const zip = new JSZip();
  const base = safeFilename(estimate.projectName || "estimate");
  const generated = new Date().toISOString();

  zip.file(`${base}_BREX_skeleton.brex.xml`, buildBrexSkeletonXml(estimate, brex));
  zip.file(`${base}_SNS.xml`, buildSnsXml(estimate.dmlPlan?.snsPresetId || "generic"));
  zip.file(`${base}_Readiness.md`, buildReadinessMarkdown(readiness, estimate, totals, resourcing, brex, dml));

  // SBS-derived DMC inventory (only when SBS contains items)
  const dmcRows = buildDmcInventoryFromSbs(estimate);
  if (dmcRows.length > 0) {
    zip.file(`${base}_DMC_inventory.csv`, buildDmcInventoryCsv(dmcRows));
  }

  // Build a compact manifest view of the DMC inventory + applicability map
  // so reviewers can join DMCs to BREX rules without opening the CSV.
  const manifestDmcInventory = dmcRows.map((r) => ({
    dmc: r.dmc,
    category: r.category,
    level: r.level,
    systemName: r.systemName,
    subsystemName: r.subsystemName || null,
    subsubsystemName: r.subsubsystemName || null,
    applicPropertyIdent: r.applicPropertyIdent,
    applicPropertyValue: r.applicPropertyValue,
  }));
  const applicabilityMap = {};
  for (const r of dmcRows) {
    const key = r.applicPropertyValue;
    if (!applicabilityMap[key]) {
      applicabilityMap[key] = { systemName: r.systemName, dmcs: [] };
    }
    applicabilityMap[key].dmcs.push(r.dmc);
  }

  const manifest = {
    name: "S1000D CSDB Seed Pack",
    project: estimate.projectName || "Untitled",
    generated,
    currency: estimate.currency?.code || "USD",
    fxFallback: !FX_SUPPORTED_CODES.includes(estimate.currency?.code || "USD"),
    s1000dIssue: estimate.brexPlan?.s1000dIssue || "6.0",
    snsPreset: estimate.dmlPlan?.snsPresetId || "generic",
    counts: {
      dataModules: totals.totalDms,
      illustrations: totals.totalIllus,
      brexDecisionPoints: brex.totalDps,
      dmcInventoryRows: dmcRows.length,
      sbsApplicabilityKeys: Object.keys(applicabilityMap).length,
      readinessOverall: readiness.overall,
      readinessScore: readiness.score,
    },
    dmcInventory: manifestDmcInventory,
    applicabilityMap,
    files: [
      `${base}_BREX_skeleton.brex.xml`,
      `${base}_SNS.xml`,
      `${base}_Readiness.md`,
      ...(dmcRows.length > 0 ? [`${base}_DMC_inventory.csv`] : []),
    ],
  };
  return { zip, manifest, base, generated };
}

export async function exportSeedPack({ estimate, totals, resourcing, brex, dml, readiness }) {
  const { zip, manifest, base } = _buildSeedPackZip({ estimate, totals, resourcing, brex, dml, readiness });
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, `${base}_CSDB_seed_pack.zip`);
  return manifest;
}

/**
 * Like exportSeedPack but returns {blob, filename, manifest} instead of triggering
 * a download. Used to upload a shareable copy to the backend.
 */
export async function buildCsdbSeedPackBlob({ estimate, totals, resourcing, brex, dml, readiness }) {
  const { zip, manifest, base } = _buildSeedPackZip({ estimate, totals, resourcing, brex, dml, readiness });
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  const blob = await zip.generateAsync({ type: "blob" });
  return { blob, filename: `${base}_CSDB_seed_pack.zip`, manifest };
}
